import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { api } from '@/api/client';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { Icon } from '@/components/icons';
import styles from './AdminServicesPage.module.css';

interface ServiceItem {
  id: string;
  code: string;
  name: string;
  configured: boolean;
  academicApplicability?: string | null;
  campusAssignment?: string | null;
}

export function AdminServicesPage() {
  const { t } = useTranslation();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';

  const { state, reload, replace } = useAsyncData<ServiceItem[]>(
    `admin-services-${institutionId}`,
    () => api.admin.services.list(institutionId) as unknown as Promise<ServiceItem[]>,
    !!institutionId,
  );

  const toast = useToast();
  const [configModal, setConfigModal] = useState<ServiceItem | null>(null);
  const [form, setForm] = useState({ configured: false, academicApplicability: '', campusAssignment: '' });
  const [saving, setSaving] = useState(false);

  const handleConfigure = useCallback((svc: ServiceItem) => {
    setConfigModal(svc);
    setForm({
      configured: svc.configured,
      academicApplicability: svc.academicApplicability ?? '',
      campusAssignment: svc.campusAssignment ?? '',
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!configModal) return;
    setSaving(true);
    try {
      const updated = (await api.admin.services.configure(institutionId, configModal.code, {
        configured: form.configured,
        academicApplicability: form.academicApplicability || undefined,
        campusAssignment: form.campusAssignment || undefined,
      })) as unknown as ServiceItem;
      if (state.status === 'ready') {
        replace(state.data.map((svc) => (svc.code === updated.code ? updated : svc)));
      }
      setConfigModal(null);
      toast.success(t('admin.services.saveSuccess'));
    } catch {
      // keep the modal open so nothing typed is lost; the save can be retried
      toast.error(t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [configModal, form, institutionId, replace, state, t, toast]);

  if (state.status === 'loading') {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  if (state.status === 'error') {
    const error = state.error;
    return (
      <div className={styles.page}>
        <Card className={styles.emptyState}>
          <Icon name={error.isMissingEndpoint ? 'info' : 'error'} size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.services.title')}</h2>
          <p className={styles.emptyStateBody}>
            {t(error.isMissingEndpoint ? 'platform.failure.missing.body' : 'platform.failure.generic.body')}
          </p>
          <button className={styles.retryButton} onClick={reload}>{t('common.retry')}</button>
        </Card>
      </div>
    );
  }

  const services = state.data;

  return (
    <div className={styles.page}>
      <div className="page-header page-headerRow">
        <div>
          <h1>{t('admin.services.title')}</h1>
          <p className="page-subtitle">{t('admin.services.subtitle')}</p>
        </div>
      </div>

      {services.length === 0 ? (
        <Card className={styles.emptyState}>
          <p className={styles.emptyStateBody}>{t('admin.services.noServices')}</p>
        </Card>
      ) : (
        <div className={styles.cardGrid}>
          {services.map((svc) => (
            <Card key={svc.id} className={styles.serviceCard}>
              <div className={styles.cardHeader}>
                <h3 className={styles.cardName}>{t(`admin.services.${svc.code}`, svc.name)}</h3>
                <span className={`${styles.statusBadge} ${svc.configured ? styles.statusConfigured : styles.statusNotConfigured}`}>
                  {t(svc.configured ? 'admin.services.configured' : 'admin.services.notConfigured')}
                </span>
              </div>
              <div className={styles.cardMeta}>
                <div className={styles.metaRow}>
                  <span className={styles.metaLabel}>{t('admin.services.academicApplicability')}</span>
                  <span className={styles.metaValue}>{svc.academicApplicability || '—'}</span>
                </div>
                <div className={styles.metaRow}>
                  <span className={styles.metaLabel}>{t('admin.services.campusAssignment')}</span>
                  <span className={styles.metaValue}>{svc.campusAssignment || '—'}</span>
                </div>
              </div>
              <button type="button" className={styles.configureBtn} onClick={() => handleConfigure(svc)}>
                {t('admin.services.configure')}
              </button>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={!!configModal}
        onClose={() => setConfigModal(null)}
        closeOnBackdropClick={false}
        title={t('admin.services.configureService')}
        description={configModal ? t('admin.services.serviceDetailDescription') : undefined}
      >
        {configModal && (
          <>
            <div className={styles.fieldRow}>
              <label className={styles.fieldLabel} htmlFor="svc-name">{t('admin.services.serviceName')}</label>
              <input
                id="svc-name"
                className={styles.fieldInput}
                type="text"
                value={t(`admin.services.${configModal.code}`, configModal.name)}
                disabled
              />
            </div>
            <div className={styles.fieldRow}>
              <label className={styles.fieldLabel} htmlFor="svc-configured">{t('admin.services.status')}</label>
              <label className={styles.checkboxLabel}>
                <input
                  id="svc-configured"
                  type="checkbox"
                  checked={form.configured}
                  onChange={(e) => setForm((p) => ({ ...p, configured: e.target.checked }))}
                />
                {t(form.configured ? 'admin.services.configured' : 'admin.services.notConfigured')}
              </label>
            </div>
            <div className={styles.fieldRow}>
              <label className={styles.fieldLabel} htmlFor="svc-applicability">
                {t('admin.services.academicApplicability')}
              </label>
              <input
                id="svc-applicability"
                className={styles.fieldInput}
                type="text"
                placeholder={t('admin.services.applicabilityPlaceholder')}
                value={form.academicApplicability}
                onChange={(e) => setForm((p) => ({ ...p, academicApplicability: e.target.value }))}
              />
            </div>
            <div className={styles.fieldRow}>
              <label className={styles.fieldLabel} htmlFor="svc-campus">
                {t('admin.services.campusAssignment')}
              </label>
              <input
                id="svc-campus"
                className={styles.fieldInput}
                type="text"
                placeholder={t('admin.services.campusAssignmentPlaceholder')}
                value={form.campusAssignment}
                onChange={(e) => setForm((p) => ({ ...p, campusAssignment: e.target.value }))}
              />
            </div>
            <div className={styles.modalActions}>
              <Button variant="secondary" onClick={() => setConfigModal(null)}>{t('common.cancel')}</Button>
              <Button onClick={handleSave} loading={saving} loadingLabel={t('common.loading')}>
                {t('common.save')}
              </Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
